import { Link } from 'react-router-dom';
import {
  GraduationCap,
  Target,
  Heart,
  Users,
  CheckCircle,
  ArrowRight,
  Star,
  BookOpen,
  Globe,
  Brain,
  ChartLineUp,
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

export default function About() {
  return (
    <div className="min-h-screen py-12">
      <SEO
        title="About Grade9Ready — Built for GCSE Success"
        description="Grade9Ready is the UK's GCSE revision platform for Maths, English, Science and Geography, designed to help GCSE students build confidence, improve revision and aim for Grade 9 progress."
        path="/about"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center mb-16">
          <motion.div
            className="inline-flex items-center gap-2 bg-accent border-2 border-foreground rounded-full px-4 py-2 mb-5 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <GraduationCap size={16} weight="fill" />
            <span className="text-xs sm:text-sm font-black uppercase tracking-wide">
              Built for GCSE improvement
            </span>
          </motion.div>

          <motion.h1
            className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Helping Every Student
            <span className="gradient-text block">Reach Their Potential</span>
          </motion.h1>

          <motion.p
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            Grade9Ready was built with one mission: to give UK secondary school
            students clearer, more active GCSE revision across Maths, English,
            Science and Geography, regardless of background or starting point.
          </motion.p>
        </div>

        {/* Mission Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {[
            {
              icon: GraduationCap,
              title: 'Our Mission',
              description:
                'To provide access to high-quality GCSE revision resources and help students build the knowledge, confidence and exam skills needed for stronger outcomes.',
            },
            {
              icon: Target,
              title: 'Our Approach',
              description:
                'We combine curriculum-aligned content, adaptive practice, AI-powered feedback and gamification to make learning effective, engaging and measurable.',
            },
            {
              icon: Heart,
              title: 'Our Values',
              description:
                'We believe education should be accessible, supportive and empowering. Every child deserves to understand what to improve next.',
            },
          ].map((item, i) => (
            <motion.div
              key={item.title}
              className="neo-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <div className="w-14 h-14 bg-primary rounded-lg border-2 border-foreground flex items-center justify-center mb-4 shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
                <item.icon size={28} weight="bold" className="text-white" />
              </div>

              <h2 className="font-heading font-bold text-xl mb-3">
                {item.title}
              </h2>

              <p className="text-muted-foreground">{item.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Live Subjects */}
        <div className="neo-card mb-16 bg-secondary/40">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-heading font-bold mb-3">
              What is live now?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Grade9Ready is growing into a broad GCSE revision platform. These
              subjects are currently live and available for structured revision.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Target,
                title: 'Maths',
                text: 'Topic explainers, problem mode, worked solutions and adaptive practice.',
                color: 'bg-indigo-500',
              },
              {
                icon: BookOpen,
                title: 'English',
                text: 'Language, literature, essay practice, AI feedback and examiner-style guidance.',
                color: 'bg-rose-500',
              },
              {
                icon: Brain,
                title: 'Science',
                text: 'Biology, Chemistry and Physics lessons with practice and progress tracking.',
                color: 'bg-emerald-500',
              },
              {
                icon: Globe,
                title: 'Geography',
                text: 'Physical geography, human geography, fieldwork, map skills and AQA-style exam practice.',
                color: 'bg-cyan-500',
              },
            ].map((subject, i) => (
              <motion.div
                key={subject.title}
                className="bg-background border-2 border-foreground rounded-xl p-5 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
              >
                <div
                  className={`w-11 h-11 ${subject.color} rounded-lg border-2 border-foreground flex items-center justify-center mb-4`}
                >
                  <subject.icon size={22} weight="bold" className="text-white" />
                </div>

                <h3 className="font-heading font-bold mb-2">{subject.title}</h3>
                <p className="text-sm text-muted-foreground">{subject.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* What Makes Us Different */}
        <div className="neo-card mb-16">
          <h2 className="text-2xl font-heading font-bold text-center mb-8">
            What Makes Grade9Ready Different
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              'GCSE-focused content across Maths, English, Science and Geography',
              'AI marking that gives instant What Went Well (WWW) and Even Better If (EBI) feedback',
              'Examiner-style tips that show how marks are won and lost',
              'Gamification that motivates without being childish',
              'Adaptive questioning so students can build from their current level',
              'Parent dashboards so families can support learning together',
              'Clear progression from foundations to higher-grade GCSE performance',
              'Affordable pricing with a meaningful free starting point',
            ].map((point) => (
              <div key={point} className="flex items-start gap-3">
                <CheckCircle
                  size={24}
                  weight="fill"
                  className="text-emerald-500 flex-shrink-0 mt-0.5"
                />
                <span>{point}</span>
              </div>
            ))}
          </div>
        </div>

        {/* How We Help */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {[
            {
              icon: BookOpen,
              title: 'Structured learning',
              description:
                'Students work through sections and topics rather than jumping randomly between disconnected revision tasks.',
            },
            {
              icon: Brain,
              title: 'Feedback that teaches',
              description:
                'AI-supported marking helps students understand what they did well, what was missing and how to improve next time.',
            },
            {
              icon: ChartLineUp,
              title: 'Progress visibility',
              description:
                'Students and parents can see completed work, strengths, weak areas and next priorities more clearly.',
            },
          ].map((item, i) => (
            <motion.div
              key={item.title}
              className="neo-card"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
            >
              <div className="w-12 h-12 bg-accent rounded-lg border-2 border-foreground flex items-center justify-center mb-4 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]">
                <item.icon size={24} weight="bold" />
              </div>

              <h2 className="font-heading font-bold text-lg mb-2">
                {item.title}
              </h2>

              <p className="text-muted-foreground text-sm">
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Team */}
        <div className="text-center mb-16">
          <h2 className="text-2xl font-heading font-bold mb-8">
            Built by Educators, for Families
          </h2>

          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Grade9Ready combines curriculum knowledge, EdTech design, AI tools,
            and feedback from real parent and pupil testers. The goal is simple:
            make GCSE revision more targeted, more useful and easier to stick with.
          </p>

          <div className="flex justify-center gap-4">
            <div className="w-16 h-16 bg-secondary rounded-full border-2 border-foreground flex items-center justify-center">
              <Users size={28} className="text-muted-foreground" />
            </div>

            <div className="w-16 h-16 bg-secondary rounded-full border-2 border-foreground flex items-center justify-center">
              <GraduationCap size={28} className="text-muted-foreground" />
            </div>

            <div className="w-16 h-16 bg-secondary rounded-full border-2 border-foreground flex items-center justify-center">
              <Star size={28} className="text-muted-foreground" />
            </div>
          </div>
        </div>

        {/* CTA */}
        <motion.div
          className="neo-card bg-primary text-white text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-2xl font-heading font-bold mb-4">
            Ready to Start Your Journey to Grade 9?
          </h2>

          <p className="opacity-90 mb-6 max-w-xl mx-auto">
            Join UK students and families using Grade9Ready to revise with
            structure, feedback and clearer next steps.
          </p>

          <Link
            to="/signup"
            className="inline-flex items-center gap-2 bg-white text-primary font-bold px-6 py-3 rounded-lg border-2 border-white hover:-translate-y-0.5 transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)]"
          >
            Get Started Free
            <ArrowRight size={20} weight="bold" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}